import { useCallback } from "react";

import { useModelCompareTranslation } from "constants/modelCompareData/translations/useModelCompareTranslation";
import type { MutatedProduct } from "constants/modelCompareData/types";
import { useMenu } from "@ubnt/store.core/lib/contexts/MenuContext";
import type { ButtonVariants } from "@ubnt/store.core/lib/components/Button/Button.types";
import * as S from "../CompareTable.styles";
import { getStoreHref } from "./getStoreHref";

interface RenderButtonProps {
  product: MutatedProduct;
  index: number;
  getComingSoonButtonText: (product: MutatedProduct) => string;
  showBuyButton: boolean;
  onLinkClick?: (
    event: React.MouseEvent<HTMLAnchorElement>,
    href: string,
  ) => void;
}

export const useButtonRenderer = () => {
  const { createSiteUrl, storeId } = useMenu();
  const { formatMessage } = useModelCompareTranslation();

  return useCallback(
    ({
      product,
      index,
      getComingSoonButtonText,
      showBuyButton,
      onLinkClick,
    }: RenderButtonProps) => {
      const {
        id,
        isComingSoon = undefined,
        addToCartButton = undefined,
        startTrialLink = undefined,
        learnMoreLink = undefined,
      } = product;

      const createButton = (
        label: string,
        href?: string,
        variant: ButtonVariants = "primary",
      ) => {
        const BUTTONS_WITHOUT_POINTER_EVENTS = isComingSoon;

        return (
          <S.ButtonWrap key={`${id}-${index}`}>
            <S.Button
              $removePointerEvents={Boolean(BUTTONS_WITHOUT_POINTER_EVENTS)}
              variant={variant}
              anchor={!BUTTONS_WITHOUT_POINTER_EVENTS}
              span={Boolean(BUTTONS_WITHOUT_POINTER_EVENTS)}
              href={href}
              onClick={(e) =>
                href &&
                onLinkClick?.(e as React.MouseEvent<HTMLAnchorElement>, href)
              }
            >
              {label}
            </S.Button>
          </S.ButtonWrap>
        );
      };

      // CASE: If we have a custom button, use that instead
      if (addToCartButton) return addToCartButton;

      // CASE: Coming soon
      if (
        (typeof isComingSoon === "boolean" && !!isComingSoon) ||
        (Array.isArray(isComingSoon) && isComingSoon.includes(storeId))
      ) {
        return createButton(
          getComingSoonButtonText(product),
          undefined,
          "comingSoon",
        );
      }

      // CASE: Start Trial
      if (startTrialLink) {
        return createButton(formatMessage("Start Trial"), startTrialLink);
      }

      // CASE: Learn More
      const learnMoreHref = learnMoreLink && createSiteUrl(learnMoreLink);
      if (learnMoreHref) {
        return createButton(formatMessage("Learn More"), learnMoreHref);
      }

      // CASE: Buy Now (no wrap needed)
      const storeHref = getStoreHref({ product, createSiteUrl, storeId });
      if (!storeHref || !showBuyButton) {
        return <S.CenterPlaceholderGridItem key={`${id}-${index}`} />;
      }

      // CASE: Default
      return createButton(formatMessage("Buy Now"), storeHref);
    },
    [createSiteUrl, storeId, formatMessage],
  );
};
